import { useState, useEffect, useRef, type FC } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { Breadcrumb, BreadcrumbItem } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import { getWorkers } from "../../services/workers";
import MapboxManager from "../../utils/mapboxManager";

// Set access token and default coordinates
const accessToken = import.meta.env["VITE_MAPBOX_ACCESS_TOKEN"];
if (!accessToken) {
  console.error("Mapbox access token is missing");
} else {
  console.log("Mapbox access token is present");
  mapboxgl.accessToken = accessToken;
}

const BERLIN_COORDINATES = { latitude: 52.52, longitude: 13.405 };

const WorkerLocationsPage: FC = function () {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const mapboxManager = MapboxManager.getInstance();

  // Initialize map
  useEffect(() => {
    console.log("Initializing map...");

    const containerId = "worker-locations-map";
    if (!mapContainer.current) return;

    // Ensure the container has an ID
    if (!mapContainer.current.id) {
      mapContainer.current.id = containerId;
    }

    const initMap = async () => {
      try {
        // Get map instance from manager
        map.current = await mapboxManager.getMap(containerId, {
          style: "mapbox://styles/mapbox/streets-v11",
          center: [BERLIN_COORDINATES.longitude, BERLIN_COORDINATES.latitude],
          zoom: 10,
          attributionControl: true,
          preserveDrawingBuffer: true,
        });

        // Add navigation control
        map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

        // Load workers and add markers
        await loadWorkers();
      } catch (err) {
        console.error("Error initializing map:", err);
        setError("Fehler bei der Initialisierung der Karte");
        setIsLoading(false);
      }
    };

    initMap();

    return () => {
      if (map.current) {
        // Clean up markers
        markers.current.forEach((marker) => marker.remove());
        markers.current = [];

        // Let the manager handle map cleanup
        mapboxManager.removeMap(containerId);
        map.current = null;
      }
    };
  }, []);

  const loadWorkers = async () => {
    if (!map.current) return;

    try {
      setIsLoading(true);
      setError(null);

      // Clean up existing markers
      markers.current.forEach((marker) => marker.remove());
      markers.current = [];

      const workers = await getWorkers();
      const activeWorkers = workers.filter((worker) => worker.active);

      // Add markers for each active worker with location data
      activeWorkers.forEach((worker) => {
        if (worker.current_latitude && worker.current_longitude) {
          const lat = parseFloat(worker.current_latitude);
          const lng = parseFloat(worker.current_longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            const marker = new mapboxgl.Marker()
              .setLngLat([lng, lat])
              .setPopup(
                new mapboxgl.Popup({ offset: 25 }).setHTML(
                  `<h3 class="text-sm font-medium">${worker.full_name}</h3>` +
                    `<p class="text-xs">${
                      worker.organization || "No organization"
                    }</p>` +
                    (worker.last_update
                      ? `<p class="text-xs text-gray-500">Last update: ${new Date(
                          worker.last_update
                        ).toLocaleString()}</p>`
                      : "")
                )
              )
              .addTo(map.current!);

            markers.current.push(marker);
          }
        }
      });

      setIsLoading(false);
    } catch (err) {
      console.error("Error loading workers:", err);
      setError("Fehler beim Laden der Mitarbeiter");
      setIsLoading(false);
    }
  };

  return (
    <NavbarSidebarLayout pageTitle="Mitarbeiterstandorte">
      <div className="p-4">
        <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex lg:mt-1.5">
          <div className="mb-1 w-full">
            <div className="mb-4">
              <Breadcrumb className="mb-4">
                <BreadcrumbItem href="#">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">Startseite</span>
                  </div>
                </BreadcrumbItem>
                <BreadcrumbItem>Mitarbeiterstandorte</BreadcrumbItem>
              </Breadcrumb>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                Mitarbeiterstandorte
              </h1>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="relative h-[600px] rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-100 bg-opacity-75 dark:bg-gray-700 dark:bg-opacity-75">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  Karte wird geladen...
                </div>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-red-100 bg-opacity-75 dark:bg-red-900 dark:bg-opacity-75">
                <div className="text-lg font-semibold text-red-900 dark:text-red-100">
                  {error}
                </div>
              </div>
            )}
            <div ref={mapContainer} className="h-full w-full rounded-lg" />
          </div>
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default WorkerLocationsPage;
